import type { Messages } from './messages/mn'

export type { Messages }

export type TranslateValues = Record<string, string | number>

export interface Translator {
  /** `t('auth.login.title')`, or `t('auth.otp.sentTo', { contact: email })`. */
  (key: MessageKey, values?: TranslateValues): string
  /** Narrows to a namespace: `const t = useT('auth.login')` then `t('title')`. */
  scoped: (prefix: string) => (key: string, values?: TranslateValues) => string
}

/**
 * Every leaf path in the catalog, as a union — `'auth.login.title' | ...`.
 *
 * The point is that a typo in a `t()` call fails the build instead of shipping
 * a raw key into the interface. It is derived from the Mongolian catalog, which
 * is the source of truth; the other four are typed against it, so a key that
 * exists here exists everywhere.
 */
export type MessageKey = LeafPaths<Messages>

type LeafPaths<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${LeafPaths<T[K]>}`
}[keyof T & string]

/**
 * Substitutes `{name}` placeholders. Anything the catalog asks for but the call
 * site did not supply is left in place rather than rendered as `undefined`, so
 * a missed value looks like the bug it is instead of like broken copy.
 */
function interpolate(template: string, values?: TranslateValues): string {
  if(!values) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name]
    return value === undefined || value === null ? match : String(value)
  })
}

function lookup(messages: Messages, key: string): string | undefined {
  let current: unknown = messages
  for(const segment of key.split('.')) {
    if(typeof current !== 'object' || current === null) return undefined
    current = (current as Record<string, unknown>)[segment]
  }
  return typeof current === 'string' ? current : undefined
}

/**
 * Builds the `t` function for one catalog.
 *
 * A missing key returns the key itself. That is deliberate: the alternative —
 * falling back to another language — hides the gap, and a visible
 * `auth.login.title` in the UI gets fixed, whereas a stray Mongolian sentence
 * in the Spanish interface reads as a translation choice. In practice
 * `MessageKey` means this cannot happen without someone casting around it.
 */
export function createTranslator(messages: Messages): Translator {
  const translate = ((key: MessageKey, values?: TranslateValues) => {
    const template = lookup(messages, key)
    if(template === undefined) {
      if(process.env.NODE_ENV !== 'production') console.warn(`[i18n] missing message: ${key}`)
      return key
    }
    return interpolate(template, values)
  }) as Translator

  translate.scoped = (prefix: string) => (key: string, values?: TranslateValues) =>
    translate(`${prefix}.${key}` as MessageKey, values)

  return translate
}
